import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { FootageFile, FootageUpload } from '../models/Footage';
import { firebaseStorageProvider } from './FirebaseStorageProvider';
import { fileNamingService } from './FileNamingService';
import { NamingConfig } from './FileNamingService';

export class FootageService {
  private static instance: FootageService;
  private footageCollection = collection(db, 'footage');

  private constructor() {}

  static getInstance(): FootageService {
    if (!FootageService.instance) {
      FootageService.instance = new FootageService();
    }
    return FootageService.instance;
  }

  async uploadFootage(
    companyId: string,
    userId: string,
    userName: string,
    uploadData: FootageUpload,
    namingConfig: NamingConfig,
    folderStructure: 'user' | 'date' | 'location',
    onProgress?: (progress: number) => void
  ): Promise<FootageFile> {
    const uploadDate = new Date();
    const fileExtension = uploadData.file.name.split('.').pop() || 'mp4';

    // Get the next video index for this user/date
    const videoIndex = await this.getNextVideoIndex(
      companyId,
      userId,
      uploadDate
    );

    // Generate filename
    const generatedFilename = fileNamingService.generateFootageFilename({
      userName,
      uploadDate,
      videoIndex,
      fileExtension,
      config: namingConfig,
    });

    // Generate folder path
    const folderPath = fileNamingService.generateFolderPath(
      companyId,
      userId,
      uploadDate,
      folderStructure
    );

    const fullPath = `${folderPath}/${generatedFilename}`;

    // Upload file to storage
    const uploadResult = await firebaseStorageProvider.uploadFile(
      uploadData.file,
      fullPath,
      (progress) => {
        if (onProgress) {
          onProgress(progress.percentage);
        }
      }
    );

    // Get video duration (client-side)
    const duration = await this.getVideoDuration(uploadData.file);

    // Create footage record
    const footageRef = doc(this.footageCollection);
    const footage: FootageFile = {
      id: footageRef.id,
      companyId,
      uploadedBy: userId,
      uploadedByName: userName,
      originalFilename: uploadData.file.name,
      generatedFilename,
      fileUrl: uploadResult.url,
      duration,
      fileSize: uploadData.file.size,
      uploadDate,
      incidentDate: uploadData.incidentDate,
      location: uploadData.location
        ? { name: uploadData.location }
        : undefined,
      metadata: uploadData.metadata || {},
      tags: uploadData.tags || [],
      status: 'ready',
      reviewCount: 0,
    };

    await setDoc(footageRef, {
      ...footage,
      uploadDate: Timestamp.fromDate(footage.uploadDate),
      incidentDate: footage.incidentDate
        ? Timestamp.fromDate(footage.incidentDate)
        : null,
    });

    return footage;
  }

  private async getNextVideoIndex(
    companyId: string,
    userId: string,
    date: Date
  ): Promise<number> {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    // Simplified query to avoid needing composite index
    const q = query(
      this.footageCollection,
      where('companyId', '==', companyId),
      where('uploadedBy', '==', userId)
    );

    const querySnapshot = await getDocs(q);

    // Filter by date in memory
    const todayCount = querySnapshot.docs.filter(doc => {
      const uploadDate = doc.data().uploadDate.toDate();
      return uploadDate >= startOfDay && uploadDate <= endOfDay;
    }).length;

    return todayCount + 1;
  }

  private getVideoDuration(file: File): Promise<number> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(Math.floor(video.duration));
      };

      video.onerror = () => {
        resolve(0);
      };

      video.src = URL.createObjectURL(file);
    });
  }

  async getFootage(footageId: string): Promise<FootageFile | null> {
    const footageRef = doc(this.footageCollection, footageId);
    const footageSnap = await getDoc(footageRef);

    if (!footageSnap.exists()) {
      return null;
    }

    const data = footageSnap.data();
    return {
      ...data,
      id: footageSnap.id,
      uploadDate: data.uploadDate.toDate(),
      incidentDate: data.incidentDate?.toDate(),
    } as FootageFile;
  }

  async getFootageByCompany(
    companyId: string,
    limitCount: number = 50
  ): Promise<FootageFile[]> {
    // Simplified query without orderBy to avoid composite index requirement
    const q = query(
      this.footageCollection,
      where('companyId', '==', companyId),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);

    const footage = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        uploadDate: data.uploadDate.toDate(),
        incidentDate: data.incidentDate?.toDate(),
      } as FootageFile;
    });

    // Sort by upload date in memory (descending - newest first)
    return footage.sort((a, b) => b.uploadDate.getTime() - a.uploadDate.getTime());
  }

  async updateFootageStatus(
    footageId: string,
    status: FootageFile['status']
  ): Promise<void> {
    const footageRef = doc(this.footageCollection, footageId);
    await updateDoc(footageRef, { status });
  }

  async incrementReviewCount(footageId: string): Promise<void> {
    const footage = await this.getFootage(footageId);
    if (!footage) throw new Error('Footage not found');

    const footageRef = doc(this.footageCollection, footageId);
    await updateDoc(footageRef, {
      reviewCount: footage.reviewCount + 1,
    });
  }

  async deleteFootage(footageId: string): Promise<void> {
    const footage = await this.getFootage(footageId);
    if (!footage) throw new Error('Footage not found');

    // Delete from storage
    const path = this.extractPathFromUrl(footage.fileUrl);
    if (path) {
      await firebaseStorageProvider.deleteFile(path);
    }

    // Delete from Firestore (we'd use deleteDoc here, but keeping simple)
    const footageRef = doc(this.footageCollection, footageId);
    await updateDoc(footageRef, { status: 'archived' });
  }

  private extractPathFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const pathMatch = urlObj.pathname.match(/\/o\/(.+)\?/);
      return pathMatch ? decodeURIComponent(pathMatch[1]) : null;
    } catch {
      return null;
    }
  }
}

// Singleton instance
export const footageService = FootageService.getInstance();
